import { Component, type ReactNode } from 'react'

/**
 * กันจอดำ
 *
 * แอปนี้ไม่มี error boundary มาก่อน แปลว่า error ตอน render เพียงจุดเดียว
 * จะถอด React ทั้ง root ออก เหลือหน้าเปล่า ๆ ที่ผู้ใช้อธิบายไม่ได้ว่าเกิดอะไร
 * และคนแก้ก็ต้องเดาเพราะไม่มีข้อความอะไรให้เห็นเลย
 *
 * boundary นี้เปลี่ยนจอดำให้กลายเป็นข้อความที่อ่านได้ พร้อมปุ่มโหลดใหม่
 * และพิมพ์ stack ลง console ให้ไล่ต่อได้ — บั๊กจะยังต้องแก้ที่ต้นเหตุ
 * แต่จะไม่ทำให้ทั้งแอปหายไปทั้งหน้าอีก
 */
interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: unknown) {
    // เก็บไว้ใน console ให้เห็น stack เต็ม — ป้ายบนจอย่อไว้พออ่าน
    console.error('ErrorBoundary จับ error ตอน render:', error, info)
  }

  override render() {
    const { error } = this.state
    if (error === null) return this.props.children

    return (
      <div className="grid min-h-screen place-items-center p-6">
        <div className="max-w-lg space-y-4 rounded-2xl border border-risk/30 bg-risk/8 p-8 text-center">
          <p className="text-sm font-semibold text-risk">
            เกิดข้อผิดพลาดจนแสดงหน้านี้ไม่ได้
            <span className="ml-1.5 font-normal text-faint">· Something went wrong</span>
          </p>
          <p className="text-xs leading-relaxed text-muted">
            ลองโหลดหน้าใหม่อีกครั้ง ถ้ายังเป็นอยู่ ช่วยส่งข้อความด้านล่างให้ผู้ดูแลระบบ
            <span className="mt-1 block text-faint">
              Try reloading. If it keeps happening, send the message below to an administrator.
            </span>
          </p>
          <pre className="overflow-x-auto rounded-lg border border-line bg-black/30 p-3 text-left text-[11px] whitespace-pre-wrap text-ink-300">
            {error.message}
          </pre>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex min-h-11 items-center justify-center rounded-xl border border-line px-5 text-sm transition-colors hover:border-line-strong"
          >
            โหลดหน้าใหม่ · Reload
          </button>
        </div>
      </div>
    )
  }
}
